import {
  type CascoCampaign,
  type GeneratorKey,
  leadGenerationCampaignsListOptions
} from '@crm/api'
import {
  CampaignStatusTag,
  GeneratorTag
} from '@crm/features/lead-generation/components'
import { formatRunTime } from '@crm/features/lead-generation/utils'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import { defaultFilters } from '@crm/utils'
import {
  ConditionalText,
  TableActions,
  TableContainer,
  TitleSection
} from '@shared/components'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useQuery } from '@tanstack/react-query'
import { Typography } from 'antd'
import { Eye } from 'iconsax-react'
import { useCallback, useMemo } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const { Text } = Typography

export const LeadGenerationCampaigns = () => {
  const { t } = useLocales()
  const { params } = useQueryUpdate()
  const navigate = useAppNavigate()

  const { data, isLoading } = useQuery(
    leadGenerationCampaignsListOptions({
      query: { ...defaultFilters, ...params }
    })
  )

  const onRow = ({ generator_key }: CascoCampaign) => ({
    onClick: () => navigate(paths.leadGeneration.campaign(generator_key))
  })

  // The row itself navigates, so the actions cell must not bubble its clicks.
  const onCell = () => ({
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    onPointerDown: (e: React.MouseEvent) => e.stopPropagation()
  })

  const getActionColumnItem = useCallback(
    (generatorKey: GeneratorKey): MenuProps['items'] => [
      {
        label: t('buttons.view'),
        key: 'view',
        icon: <Eye size={20} />,
        onClick: () => navigate(paths.leadGeneration.campaign(generatorKey))
      }
    ],
    [t, navigate]
  )

  const columns: TableColumnsType<CascoCampaign> = useMemo(
    () => [
      {
        title: t('lead_generation.campaign'),
        key: 'name',
        render: ({ name }) => (
          <ConditionalText condition={!!name}>
            <Text strong>{name}</Text>
          </ConditionalText>
        )
      },
      {
        title: t('lead_generation.status'),
        key: 'is_active',
        render: ({ is_active }) => <CampaignStatusTag isActive={is_active} />
      },
      {
        title: t('lead_generation.generator'),
        key: 'generator_key',
        render: ({ generator_key }) => (
          <GeneratorTag generatorKey={generator_key} />
        )
      },
      {
        title: t('lead_generation.run_time'),
        key: 'run_time',
        render: ({ run_time }) => formatRunTime(run_time)
      },
      {
        width: 50,
        fixed: 'right',
        key: 'actions',
        onCell,
        render: ({ generator_key }) => (
          <TableActions options={getActionColumnItem(generator_key)} />
        )
      }
    ],
    [t, getActionColumnItem]
  )

  return (
    <>
      <TitleSection
        title={t('menu.lead_generation')}
        count={data?.count}
        isLoading={isLoading}
      />

      <TableContainer
        rowKey="generator_key"
        data={data}
        columns={columns}
        loading={isLoading}
        isHoverAlternative
        rowClassName="pointer"
        showSearch
        showSorterTooltip={false}
        onRow={onRow}
      />
    </>
  )
}
